import axios from "axios";
import { Config } from "../latest/src/constants/AppServerConfig";
import Storage from "../latest/src/utils/Storage";
import store, { history } from "./../redux/store";
import { setFlashNotification } from "../redux/modules/flashNotification";
import { Logger } from 'src/latest/src/services';
import { clearAccessToken, getAccessToken, refreshAccessToken } from "src/latest/src/auth/authStore";
import { whenSessionReady } from "src/latest/src/auth/sessionStartup";

export const api = axios.create({
    baseURL: Config.apiUrl,
    headers: {
        "Content-Type": "application/json",
    },
    timeout: 180000, // for intake
});
api.interceptors.request.use((config) => {
    return config;
});

/**
 * Refresh failed, or came back without a usable token — drop local state and
 * reload so the app re-runs its auth bootstrap.
 *
 * The reload is guarded by the same path check as the storage clear. It used
 * to sit outside the `if` (the braces were missing), which meant a failed
 * refresh on /login reloaded the login page in a loop.
 */
const clearSessionAndReload = () => {
    Storage.clear();
    // the reload below is skipped on /login, and nothing else resets memory
    clearAccessToken();

    if (
        !window.location.pathname.includes("login") &&
        !window.location.pathname.includes("sign-up") &&
        window.location.pathname !== "/"
    ) {
        window.location.reload();
    }
};

/** this interceptor only hanldes error case **/
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const statusCode = error?.response?.status; // this should not change from BE response
        const err =
            error?.response?.data?.error || error?.response?.data?.exception;

        const errorMessage = error?.response?.data?.message;

        // if (
        //      errorMessage && errorMessage.includes("Transaction Rejected") &&
        //     errorMessage.includes("400")
        // ) {
        //     throw error;
        // }

        /**
         * Access token expired.
         *
         * One handler for both resilient and non-resilient requests — this was
         * two byte-for-byte duplicated blocks that had already drifted apart in
         * their error handling.
         *
         * refreshAccessToken() is single-flight, so concurrent 401s all await
         * one refresh call instead of each starting their own. That replaces a
         * 50ms setInterval poll against a module-level variable.
         */
        if (error.response && statusCode === 401 && err === "invalid_token") {
            try {
                // persists the new credential itself
                const response = await refreshAccessToken();

                error.config.headers.Authorization = `Bearer ${response.access_token}`;

                // replay through `api`, not bare axios, so the retry keeps the
                // instance's baseURL and timeout
                return api(error.config);
            } catch (e) {
                console.error("error while refreshing token", e);
                clearSessionAndReload();

                throw error;
            }
        }

        if (error.response && !error.response?.config.isResilient) {
            if (
                errorMessage &&
                errorMessage.includes("Transaction Rejected") &&
                errorMessage.includes("400")
            ) {
                throw error;
            } else if (
                error.response.status !== 409 &&
                error.response.status !== 400 &&
                error.response.status === 500 &&
                error.response.config.url !==
                    "ehr-connect-service/ehr/upsert_user" &&
                !window.location.pathname.includes("/error")
            ) {
                /**
                 * 409 - error comes when you invite and existing user
                 * **/
                Storage.setItem(
                    "error",
                    JSON.stringify({
                        err: error.response.data,
                        loc: `${error.response.config.baseURL}/${error.response.config.url}`,
                    })
                );
                if (
                    !window.location.pathname.includes("login") &&
                    !window.location.pathname.includes("sign-up") &&
                    window.location.pathname !== "/" &&
                    !window.location.pathname.includes("change-password")
                )
                    history.push("/error");
            }

            throw error; // throw the error back to function, where is was getting called
        } else if (
            !error.response &&
            window.location.origin.includes("localhost")
        ) {
            // for localhost when BE logs you out
            // clear all the storage
            localStorage.clear();
            sessionStorage.clear();
            store.dispatch(
                setFlashNotification({
                    message: "Session expired",
                })
            );
            // redirect
            window.location.pathname = "/login";
        } else {
            Logger.error(error);
            // throw error;
        }
    }
);

/**
 * Serialize javascript object for sending to api
 * @param {Object} data
 * @returns {String}
 */
export function serialize(data: Object) {
    return Object.keys(data)
        .map((keyName) => {
            return `${encodeURIComponent(keyName)}=${
                data[keyName] ? encodeURIComponent(data[keyName]) : ""
            }`;
        })
        .join("&");
}

/**
 * Method for making ajax calls to the site's api
 * @param {String} endpoint - the endpoint url
 * @param {String} method api method POST | GET | DELETE
 * @param {Object|String} [data] - key:value pairs of the data to be sent to server
 * @param {String} contentType - header contentType of request
 * @param {String} authType
 * @param {Boolean} isResilientAPI
 * @returns {Promise}
 */
export default async function makeApiRequest(
    endpoint: string,
    method?: string,
    data?: any,
    contentType?: string,
    authType?: string,
    isResilientAPI?: boolean
) {
    // Startup may still be re-establishing the session, and the header below
    // needs the token that lands when it does. Resolves immediately once the
    // bootstrap has settled, and for every call made after it.
    await whenSessionReady();

    const request = {
        method,
        url: endpoint,
        data: data ? (endpoint.includes("oauth") && !endpoint.includes('g-calendar') ? serialize(data) : data) : "",
        "Access-Control-Max-Age": 600,
    };

    if (endpoint.includes("oauth") && !endpoint.includes("g-calendar")) {
        request.headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${Config.clientCredentialInternal}`,
        };
    } else {
        if (endpoint.includes("signup")) {
            const tenantId = sessionStorage.getItem("TENANT_ID");

            request.headers = {
                "practice-id": tenantId,
            };
        } else {
            request.headers = {
                Authorization: `Bearer ${getAccessToken()}`,
            };
        }

        if (contentType) {
            request.headers["Content-Type"] = contentType;
        } else if (endpoint.includes("convert-to-pdf")) {
            request.headers["Content-Type"] = "application/json";
            request.headers.Accept = "application/pdf";
            request.responseType = "blob";
        }
    }

    // if a authorisation header contains invalid token , delete the authorisation header
    if (request?.headers?.Authorization?.length < 15) {
        delete request.headers.Authorization;
    }

    if (authType === "basic_auth" || request.url === "token-service/token") {
        const authToken = Config.clientCredentialInternal;
        // this is to pass client credential to token service apis

        request.headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Basic ${authToken}`,
        };
    }

    request.isResilient = isResilientAPI || false;

    const response = await api(request);

    if (response && response.status === 200) {
        const location = response.request.responseURL; // redirection url return by previous API call.

        if (location.includes("login?code=")) {
            // if challenge code returned
            return location; // instead of redirecting we return request url handled in login.
        } else if (location.includes("login") && !location.includes("code=")) {
            // clear all the storage
            localStorage.clear();
            sessionStorage.clear();
            store.dispatch(
                setFlashNotification({
                    message: "Session expired",
                })
            );
            // redirect
            window.location.href = location; // redirect to the url
        } else {
            return response.data;
        }
    } else {
        const error = new Error(response?.statusText || "Unknow error occured");

        error.response = response;
        throw error;
    }
}

/**
 * Method for making ajax calls to the site's api
 * @param {String} apiUrl - the api url
 * @param {String} endpoint - the endpoint url
 * @param {Object|string} [data] - key:value pairs of the data to be sent to server
 * @returns {Promise}
 */
export async function makeExternalRequest(apiUrl, endpoint, data = null) {
    const url = `${apiUrl}${endpoint}${data ? `?${serialize(data)}` : ""}`;
    const response = await fetch(url);

    if (response.ok) {
        const data = await response.json();

        return data;
    } else {
        const error = new Error(response.statusText);

        error.response = response;
        throw error;
    }
}
